import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
} from '@mui/material';
import PropTypes from 'prop-types';

/**
 * PaginationControls Component
 * Controls for pagination, sorting, and rows per page
 */
const PaginationControls = ({
  page,
  rowsPerPage,
  totalExecutions,
  sortBy,
  sortOrder,
  onPageChange,
  onRowsPerPageChange,
  onSortChange,
}) => {
  const totalPages = Math.ceil(totalExecutions / rowsPerPage);
  const startIndex = page * rowsPerPage + 1;
  const endIndex = Math.min((page + 1) * rowsPerPage, totalExecutions);

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        backgroundColor: 'grey.50',
        borderRadius: 1,
        display: 'flex',
        gap: 2,
        alignItems: 'center',
        flexWrap: 'wrap',
        border: '1px solid',
        borderColor: 'grey.200',
      }}
    >
      {/* Pagination Info */}
      <Typography variant="body2" color="text.secondary">
        Showing {startIndex}-{endIndex} of {totalExecutions} executions
      </Typography>

      {/* Rows Per Page Selector */}
      <FormControl size="small" sx={{ minWidth: 120 }}>
        <InputLabel>Rows</InputLabel>
        <Select value={rowsPerPage} label="Rows" onChange={onRowsPerPageChange}>
          <MenuItem value={5}>5</MenuItem>
          <MenuItem value={10}>10</MenuItem>
          <MenuItem value={25}>25</MenuItem>
          <MenuItem value={50}>50</MenuItem>
        </Select>
      </FormControl>

      {/* Sort Controls */}
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel>Sort By</InputLabel>
        <Select value={sortBy} label="Sort By" onChange={onSortChange}>
          <MenuItem value="created_at">Created Date</MenuItem>
          <MenuItem value="execution_time">Execution Time</MenuItem>
          <MenuItem value="cost">Cost</MenuItem>
          <MenuItem value="model_used">Model</MenuItem>
          <MenuItem value="provider_used">Provider</MenuItem>
        </Select>
      </FormControl>

      <Typography variant="body2" color="text.secondary">
        {sortOrder === 'desc' ? '↓ Descending' : '↑ Ascending'}
      </Typography>

      {/* Page Navigation */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          size="small"
          disabled={page === 0}
          onClick={() => onPageChange(null, 0)}
        >
          First
        </Button>
        <Button
          size="small"
          disabled={page === 0}
          onClick={() => onPageChange(null, page - 1)}
        >
          Previous
        </Button>
        <Typography variant="body2" sx={{ mx: 1 }}>
          Page {page + 1} of {totalPages}
        </Typography>
        <Button
          size="small"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(null, page + 1)}
        >
          Next
        </Button>
        <Button
          size="small"
          disabled={page >= totalPages - 1}
          onClick={() => onPageChange(null, totalPages - 1)}
        >
          Last
        </Button>
      </Box>
    </Box>
  );
};

PaginationControls.propTypes = {
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  totalExecutions: PropTypes.number.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
};

export default PaginationControls;
